/**
 * WAHA realtime manager — raw WebSocket + JSON channel subscribe.
 *
 * Subscribes to `branch:{code}` for booking / planning / table events.
 */

import type { AppDispatch } from '@/app/store/store';
import { buildWahaWebSocketUrl } from '@/app/config/whatsapp-api-env';
import { extractSocketEventFromBody } from '@/shared/socket/event-router';
import { realtimeEventProcessor } from '@/shared/socket/realtime-event-processor';
import {
  buildWahaHealthFrame,
  buildWahaSubscribeFrame,
  buildWahaUnsubscribeFrame,
  buildWahaUnsubscribeFramesForBranch,
  getWahaBranchChannel,
  WAHA_SOCKET_HEALTH_CHECK_INTERVAL_MS,
  WAHA_SOCKET_MAX_RECONNECT_ATTEMPTS,
  WAHA_SOCKET_RECONNECT_DELAY_MS,
} from '@/shared/socket/waha/config';
import { parseWahaSocketBody } from '@/shared/socket/waha/parse-waha-socket-message';
import { publishWahaSessionStatus } from '@/shared/socket/waha/session-status-bus';
import {
  closeWahaRawSocket,
  connectWahaRawSocket,
  isWahaRawSocketOpen,
} from '@/shared/socket/waha/waha-raw-websocket';

type WahaSocketStartArgs = {
  dispatch: AppDispatch;
  token: string;
  branchCode: string;
  locale: string;
};

type ConnectionKey = string;

class WahaSocketManager {
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private healthCheckTimer: ReturnType<typeof setInterval> | null = null;
  private visibilityListenerAttached = false;
  private activeKey: ConnectionKey | null = null;
  private connectionSession = 0;
  private reconnectAttempts = 0;
  private reconnectExhausted = false;
  private intentionalStop = false;
  private subscribedChannels = new Set<string>();
  private pendingDispatch: AppDispatch | null = null;
  private pendingBranchCode: string | null = null;
  private pendingToken: string | null = null;
  private pendingLocale = 'en';

  start({ dispatch, token, branchCode, locale }: WahaSocketStartArgs) {
    const normalizedToken = token.trim();
    const normalizedBranch = branchCode.trim();
    const normalizedLocale = locale.split('-')[0] || 'en';
    const key = `${normalizedBranch}|${normalizedLocale}|${normalizedToken.slice(-12)}`;

    if (!normalizedToken || !normalizedBranch) {
      this.stop();
      return;
    }

    if (this.reconnectExhausted && this.activeKey === key) {
      return;
    }

    this.pendingDispatch = dispatch;
    this.pendingBranchCode = normalizedBranch;
    this.pendingToken = normalizedToken;
    this.pendingLocale = normalizedLocale;

    const channelsMatch =
      this.activeKey === key &&
      isWahaRawSocketOpen(this.socket) &&
      this.hasExpectedSubscriptions(normalizedBranch);

    if (channelsMatch) {
      return;
    }

    this.stop({ intentional: true });
    this.reconnectExhausted = false;
    this.reconnectAttempts = 0;
    this.activeKey = key;
    this.intentionalStop = false;

    this.pendingDispatch = dispatch;
    this.pendingBranchCode = normalizedBranch;
    this.pendingToken = normalizedToken;
    this.pendingLocale = normalizedLocale;

    this.attachVisibilityListener();
    this.openConnection();
  }

  stop(options?: { intentional?: boolean }) {
    this.intentionalStop = options?.intentional ?? true;
    const branchCode = this.pendingBranchCode?.trim();
    this.connectionSession += 1;
    this.clearReconnectTimer();
    this.clearHealthCheck();
    this.detachVisibilityListener();

    const unsubscribeFrames = branchCode
      ? buildWahaUnsubscribeFramesForBranch(branchCode)
      : [...this.subscribedChannels].map(buildWahaUnsubscribeFrame);

    closeWahaRawSocket(this.socket, { unsubscribeFrames });
    this.socket = null;
    this.subscribedChannels.clear();
    this.activeKey = null;
    this.pendingDispatch = null;
    this.pendingBranchCode = null;
    this.pendingToken = null;
    this.reconnectExhausted = false;
    this.reconnectAttempts = 0;
    realtimeEventProcessor.reset();
  }

  private hasExpectedSubscriptions(branchCode: string): boolean {
    return this.subscribedChannels.has(getWahaBranchChannel(branchCode));
  }

  private subscribeChannel(channel: string) {
    if (
      !isWahaRawSocketOpen(this.socket) ||
      this.subscribedChannels.has(channel)
    ) {
      return;
    }

    try {
      this.socket?.send(buildWahaSubscribeFrame(channel));
      this.subscribedChannels.add(channel);
    } catch (error) {
      console.warn('[waha-socket] subscribe failed', channel, error);
    }
  }

  private openConnection() {
    const token = this.pendingToken?.trim();
    const branchCode = this.pendingBranchCode?.trim();
    const locale = this.pendingLocale;

    if (!token || !branchCode || this.intentionalStop) {
      return;
    }

    const sessionId = ++this.connectionSession;
    const url = buildWahaWebSocketUrl({ token, branchCode, locale });
    const unsubscribeFrames = buildWahaUnsubscribeFramesForBranch(branchCode);

    this.clearHealthCheck();
    closeWahaRawSocket(this.socket, { unsubscribeFrames });
    this.socket = null;
    this.subscribedChannels.clear();

    const ws = connectWahaRawSocket(url, {
      onOpen: () => {
        if (sessionId !== this.connectionSession) {
          return;
        }

        this.reconnectAttempts = 0;
        this.subscribedChannels.clear();
        this.subscribeChannel(getWahaBranchChannel(branchCode));
        this.startHealthCheck(sessionId);
      },
      onMessage: (rawBody) => {
        if (sessionId !== this.connectionSession) {
          return;
        }

        this.handleMessage(rawBody);
      },
      onClose: () => {
        if (sessionId !== this.connectionSession) {
          return;
        }

        this.clearHealthCheck();
        this.socket = null;
        this.subscribedChannels.clear();
        this.scheduleReconnect(sessionId);
      },
      onError: (error) => {
        if (sessionId !== this.connectionSession) {
          return;
        }

        console.warn('[waha-socket] WebSocket error', error);
      },
    });

    this.socket = ws;
  }

  private handleMessage(rawBody: string) {
    try {
      const parsed = parseWahaSocketBody(rawBody);
      const dispatch = this.pendingDispatch;
      const branchCode = this.pendingBranchCode?.trim();

      if (!dispatch || !branchCode) {
        return;
      }

      if (parsed.sessionMessage) {
        publishWahaSessionStatus(parsed.sessionMessage);
        return;
      }

      if (parsed.isBookingRealtimeEvent && parsed.bookingRealtimeBody) {
        realtimeEventProcessor.enqueueOrProcess({
          dispatch,
          parsedBody: parsed.bookingRealtimeBody,
          eventName: extractSocketEventFromBody(parsed.bookingRealtimeBody),
        });
      }
    } catch (error) {
      console.error('[waha-socket] message handling failed', error);
    }
  }

  private scheduleReconnect(sessionId: number) {
    if (this.intentionalStop || this.reconnectExhausted) {
      return;
    }

    if (!this.activeKey || !this.pendingBranchCode || !this.pendingToken) {
      return;
    }

    this.reconnectAttempts += 1;

    if (this.reconnectAttempts > WAHA_SOCKET_MAX_RECONNECT_ATTEMPTS) {
      this.reconnectExhausted = true;
      closeWahaRawSocket(this.socket);
      this.socket = null;
      return;
    }

    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      if (sessionId !== this.connectionSession || this.intentionalStop) {
        return;
      }

      this.openConnection();
    }, WAHA_SOCKET_RECONNECT_DELAY_MS);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private startHealthCheck(sessionId: number) {
    this.clearHealthCheck();
    this.sendHealthCheck();

    this.healthCheckTimer = setInterval(() => {
      if (sessionId !== this.connectionSession) {
        this.clearHealthCheck();
        return;
      }

      this.sendHealthCheck();
    }, WAHA_SOCKET_HEALTH_CHECK_INTERVAL_MS);
  }

  private sendHealthCheck() {
    if (
      !isWahaRawSocketOpen(this.socket) ||
      this.subscribedChannels.size === 0
    ) {
      return;
    }

    try {
      this.socket?.send(buildWahaHealthFrame());
    } catch (error) {
      console.warn('[waha-socket] health check send failed', error);
    }
  }

  private clearHealthCheck() {
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }
  }

  private attachVisibilityListener() {
    if (this.visibilityListenerAttached || typeof document === 'undefined') {
      return;
    }

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.visibilityListenerAttached = true;
  }

  private detachVisibilityListener() {
    if (!this.visibilityListenerAttached || typeof document === 'undefined') {
      return;
    }

    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );
    this.visibilityListenerAttached = false;
  }

  private handleVisibilityChange = () => {
    if (document.visibilityState !== 'visible') {
      this.clearHealthCheck();
      return;
    }

    if (this.intentionalStop || !this.activeKey) {
      return;
    }

    const branchCode = this.pendingBranchCode?.trim() ?? '';

    if (
      isWahaRawSocketOpen(this.socket) &&
      this.hasExpectedSubscriptions(branchCode)
    ) {
      this.reconnectExhausted = false;
      this.reconnectAttempts = 0;
      this.startHealthCheck(this.connectionSession);
      return;
    }

    this.reconnectExhausted = false;
    this.reconnectAttempts = 0;
    this.openConnection();
  };
}

export const wahaSocketManager = new WahaSocketManager();
